export interface Doctor {
  id: string;
  name: string;
  title: string;
  role: string;
  bio: string;
  states: string[];
  highlights: string[];
  verificationUrl: string;
  image: string;
  /** Contact email used for auto-assignment. Must match the email in doctor_contacts / doctor_profiles table. */
  email: string;
  /** NPI number — if present, shows NPI Verified badge on listing and profile pages */
  npi_number?: string | null;
  /** State-specific license numbers keyed by state code */
  stateLicenses?: Record<string, string>;
}

export interface StateOption {
  code: string;
  name: string;
}

export const DOCTORS: Doctor[] = [
  {
    id: "stephanie-white",
    name: "Stephanie White",
    title: "LCSW",
    role: "Licensed Clinical Social Worker",
    bio: "Stephanie White, PsyD, LCSW, DCSW, BCD, is a highly experienced clinical social worker and doctoral-level clinician specializing in psychotherapy across outpatient, inpatient, and telehealth settings. With advanced training in clinical psychology, she provides comprehensive mental health evaluations and personalized treatment for conditions such as anxiety, depression, and emotional distress. She is dedicated to helping individuals achieve improved well-being, greater self-sufficiency, and lasting positive change.",
    states: ["AL", "AK", "CA", "CO", "DE", "FL", "HI", "IA", "KS", "KY", "LA", "MT", "NE", "NJ", "ND", "OR", "VT", "WV", "WI", "WY"],
    highlights: ["LCSW", "Telehealth Evaluations", "Anxiety & Depression", "ESA Letters"],
    verificationUrl: "https://growtherapy.com/provider/6e9dqjnwtec0/dr-stephanie-white",
    image: "/assets/providers/provider-stephanie-white.jpg",
    email: "swmagnet@gmail.com",
    npi_number: "1528423795",
    stateLicenses: {
      AL: "4870C",
      AK: "CSWS1004",
      CA: "24894",
      CO: "CSW.00000334",
      DE: "Q1-0012516",
      FL: "SW6787",
      HI: "4416",
      IA: "092478",
      ID: "29960",
      KS: "5133",
      KY: "254822",
      LA: "15605",
      MT: "BBH-LCSW-LIC-36995",
      NE: "IMHP 2057",
      NJ: "44SC05863400",
      ND: "5630",
      NV: "7193-C",
      OR: "L15518",
      TX: "60051",
      VT: "089.0134183",
      WV: "DP00945408",
      WI: "8957-123",
      WY: "1141",
    },
  },
  {
    id: "robert-staaf",
    name: "Robert Staaf",
    title: "LCSW",
    role: "Board-Certified Clinical Social Worker",
    bio: "Robert Staaf, LCSW, is a licensed clinical therapist experienced in supporting individuals with anxiety, depression, and emotional challenges. He also conducts thorough evaluations for Emotional Support Animals (ESAs), helping clients access the support they need for improved mental well-being. Known for his approachable and compassionate style, Robert creates a safe, judgment-free space for meaningful growth.",
    states: ["AL", "AZ", "CO", "CT", "DC", "FL", "GA", "ID", "IL", "IN", "KY", "ME", "MD", "MA", "MI", "MN", "MS", "MT", "NV", "NH", "NJ", "NM", "NC", "OH", "OK", "RI", "SC", "TX", "UT", "VT", "VA"],
    highlights: ["LCSW", "Multi-State Licensed", "Clinical Assessments", "Telehealth Consultations"],
    verificationUrl: "https://care.headway.co/providers/robert-staaf",
    image: "/assets/providers/provider-robert-staaf.jpg",
    email: "robertstaaftherapy@gmail.com",
    npi_number: "1467172478",
  },
  {
    id: "michelle-lafferty",
    name: "Michelle Lafferty",
    title: "LCSW",
    role: "Licensed Clinical Social Worker",
    bio: "Michelle Lafferty is a licensed clinical social worker with extensive experience supporting individuals navigating anxiety, depression, life transitions, and emotional wellness. She takes a compassionate, client-centered approach to mental health evaluations and is committed to helping clients access the support they need — including ESA letters — through a thorough and respectful clinical process.",
    states: ["NC", "PA", "VA"],
    highlights: ["LCSW", "Telehealth Evaluations", "Anxiety & Depression", "ESA Letters"],
    verificationUrl: "https://pawtenant.com/join-our-network",
    image: "/assets/providers/provider-michelle-lafferty.jpg",
    email: "mjones2378@yahoo.com",
    npi_number: null,
  },
  {
    id: "lytara-garcia",
    name: "Lytara Garcia",
    title: "LCSW",
    role: "ESA Mental Health Specialist",
    bio: "Lytara Garcia is a licensed clinical social worker experienced in working with adults facing anxiety, depression, stress, and trauma. She uses an eclectic, client-centered approach, incorporating techniques like CBT, mindfulness, and solution-focused therapy to meet each individual's needs. Lytara is passionate about helping clients build emotional resilience, develop coping strategies, and move forward with confidence.",
    states: ["AZ", "CA", "FL", "ID", "NV", "TX", "UT"],
    highlights: ["LCSW", "Trauma-Informed Care", "ESA Assessments", "Telehealth Consultations"],
    verificationUrl: "https://www.psychologytoday.com/us/therapists/lytara-garcia-las-vegas-nv/1141768",
    image: "/assets/providers/provider-lytara-garcia.jpg",
    email: "Lytara.garcia07@gmail.com",
    npi_number: "1386996213",
  },
];

// ALL_STATES is now derived from the canonical 51-entry list in src/lib/usStates.ts
// (50 states + DC). The previous inline list was missing AR, MO, PA, SD, TN.
import { US_STATES as CANONICAL_US_STATES } from "../lib/usStates";

export const ALL_STATES: StateOption[] = CANONICAL_US_STATES.map((s) => ({
  code: s.code,
  name: s.name,
}));

export const AVAILABLE_STATES: string[] = Array.from(
  new Set(DOCTORS.flatMap((d) => d.states))
).sort();

export const getPricingForPets = (petCount: number): number => {
  if (petCount === 1) return 100;
  if (petCount === 2) return 115;
  return 135;
};

export const getDoctorsForState = (stateCode: string): Doctor[] =>
  DOCTORS.filter((d) => d.states.includes(stateCode));
